import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { BloodGlucoseUnit, Hba1cTrack, UserPouchContent, WeightTrack } from '@libs/shared/interfaces/couch/models/user';
import { UserStore } from '@libs/shared/interfaces/couch/stores/user';
import { map, Observable } from 'rxjs';

const DEFAULT_BLOOD_GLUCOSE_UNIT: BloodGlucoseUnit = 'MG_DL';

@Injectable({
  providedIn: 'root',
})
export class UserQuery extends Query<UserPouchContent> {
  public user$ = this.select();

  public locale$ = this.select((user) => user.language);

  public currentLanguage$ = this.select((user) => user.language);

  public firstVisit$ = this.select((user) => user.firstVisit);

  public gender$ = this.select((user) => user.gender);

  public height$ = this.select((user) => user.measures?.height);

  public weightProgression$ = this.select((user) => this.getWeightProgression(user));

  public weightGoals$ = this.select((user) => user.goals?.weight);

  public hba1cTrack$ = this.select((user) => this.getHba1cTrack(user));

  public age$ = this.select((user) => this.getAge(user));

  public activityLevel$ = this.select((user) => user.activitylevel);

  public caloriesToSave$ = this.select((user) => this.getCaloriesToSave(user));

  public bloodGlucoseUnit$: Observable<BloodGlucoseUnit> = this.select(
    (user) => user.bloodGlucoseUnit || DEFAULT_BLOOD_GLUCOSE_UNIT,
  );

  public migrations$ = this.select((user) => user.migrations);

  constructor(protected override store: UserStore) {
    super(store);
  }

  public get fatSecretRegion(): string {
    const user = this.getValue();

    return user.fatSecretRegion;
  }

  public get weight$(): Observable<number> {
    return this.weightProgression$.pipe(map((weightTrack) => this.getWeight(weightTrack)));
  }

  public get hba1c$(): Observable<number | undefined> {
    return this.hba1cTrack$.pipe(map((track) => (track.length ? track[0].value : undefined)));
  }

  public get useSystemDefaultLanguage(): boolean {
    return this.getValue().useSystemDefaultLanguage;
  }

  public get language(): string {
    return this.getValue().language;
  }

  public get isOnlyOidcUser(): boolean {
    const user = this.getValue();

    return user.providers?.length === 1 && user.providers[0] === 'oidc';
  }

  public get isOidcConnected(): boolean {
    const user = this.getValue();

    return !!user.providers?.includes('oidc');
  }

  public get isEpaConnected(): boolean {
    const user = this.getValue();

    return !!(user.epa?.active && user.epa?.autoUpdate);
  }

  public getUser(): UserPouchContent {
    return this.getValue();
  }

  public getLocale(): string {
    return this.getValue().language;
  }

  public getCurrentLanguage(): string {
    return this.getValue().language;
  }

  public getBloodGlucoseUnit(): BloodGlucoseUnit {
    return this.getValue().bloodGlucoseUnit || DEFAULT_BLOOD_GLUCOSE_UNIT;
  }

  public isBloodGlucoseUnitSet(): Observable<boolean> {
    return this.select().pipe(
      map((user) => {
        return !!user.bloodGlucoseUnit;
      }),
    );
  }

  public getCaloriesToSave(user: UserPouchContent): number {
    const reached = user.goals?.weight?.reached ?? false;
    const caloriesToSave = user.goals?.weight?.caloriesToSave ?? 0;

    return !reached ? caloriesToSave : 0;
  }

  public getAge(user: UserPouchContent): number {
    return Math.abs(
      new Date(Date.now() - new Date(user.birthdate).getTime()).getUTCFullYear() - new Date(0).getFullYear(),
    );
  }

  public getWeightProgression(user: UserPouchContent): WeightTrack[] {
    if (!user.measures.weightTrack) {
      return [];
    }

    return user.measures?.weightTrack.sort((a, b) => {
      if (a.date > b.date) {
        return -1;
      }

      if (b.date > a.date) {
        return 1;
      }

      return 0;
    });
  }

  public getWeight(weightTrack: WeightTrack[]): number {
    return weightTrack.length ? weightTrack[0].weight : 0;
  }

  public getCurrentEmail(): string {
    const user = this.getValue();

    return user.email;
  }

  public getCurrentWeight(): number {
    const user = this.getValue();

    return user.measures.weightTrack[0]?.weight;
  }

  public getWeightTrack(): WeightTrack[] {
    const user = this.getValue();

    return user.measures.weightTrack;
  }

  public getHba1cTrack(userDoc?: UserPouchContent): Hba1cTrack[] {
    const source = userDoc ?? this.getValue();
    const track = source.measures.hba1cTrack || [];

    return track.slice().sort((a, b) => b.date.localeCompare(a.date));
  }

  public getCurrentHba1c(): number | undefined {
    const track = this.getHba1cTrack();

    return track.length ? track[0].value : undefined;
  }

  public mostFrequentWeights(): Observable<number[]> {
    return this.select().pipe(
      map((user) => user.measures.weightTrack),
      map((entries) => {
        const occurrences: Record<string, { weight: number; count: number }> = {};

        for (const entry of entries) {
          const id = entry.weight;

          if (!occurrences[id]) {
            occurrences[id] = {
              weight: entry.weight,
              count: 1,
            };

            continue;
          }

          occurrences[id].weight = entry.weight;
          occurrences[id].count += 1;
        }

        const idsSortedByCount = Object.keys(occurrences).sort((a, b) => {
          return occurrences[b].count - occurrences[a].count;
        });

        const mostFrequentWeights: number[] = [];

        for (const id of idsSortedByCount) {
          mostFrequentWeights.push(occurrences[id].weight);
        }

        return mostFrequentWeights;
      }),
    );
  }
}
